import {
  BarChart3,
  Building2,
  CreditCard,
  FileText,
  Gauge,
  LogOut,
  Mail,
  MessageSquareText,
  PhoneCall,
  Menu,
  ShieldCheck,
  Store,
  CircleUserRound,
  X,
} from "lucide-react";
import { useEffect, useState, type ReactNode } from "react";
import { NavLink } from "react-router-dom";
import type { ImpersonationState } from "@restlab/shared";
import { api } from "../api/client";
import { useAuth } from "../auth/AuthProvider";
import { Logo } from "./Logo";

const navigation = [
  { to: "/admin", label: "Resumen", icon: Gauge, end: true },
  { to: "/admin/statistics", label: "Estadísticas", icon: BarChart3 },
  { to: "/admin/groups", label: "Grupos", icon: Building2 },
  { to: "/admin/restaurants", label: "Restaurantes", icon: Store },
  { to: "/admin/plans", label: "Planes", icon: CreditCard },
  { to: "/admin/email", label: "Correo global", icon: Mail },
  { to: "/admin/sms", label: "SMS y cobros", icon: MessageSquareText },
  { to: "/admin/billing", label: "Holded y facturas", icon: FileText },
  { to: "/admin/voice", label: "RestLab Voice", icon: PhoneCall },
];

export function AdminShell({ children }: { children: ReactNode }) {
  const { user, logout } = useAuth();
  const [mobileMenu, setMobileMenu] = useState(false);
  const [impersonation, setImpersonation] = useState<ImpersonationState>({
    active: false,
  });

  useEffect(() => {
    void api<ImpersonationState>("/api/v1/admin/impersonation").then(
      setImpersonation,
    );
  }, []);

  async function exitImpersonation() {
    await api("/api/v1/admin/impersonation/exit", { method: "POST" });
    setImpersonation({ active: false });
  }

  return (
    <div className="admin-shell">
      <a className="skip-link" href="#admin-main">
        Saltar al contenido
      </a>
      <aside
        className={`admin-sidebar ${mobileMenu ? "admin-sidebar--open" : ""}`}
      >
        <div className="admin-sidebar__brand">
          <Logo onDark />
          <span className="admin-badge">ADMIN</span>
          <button
            className="icon-button mobile-only"
            onClick={() => setMobileMenu(false)}
            aria-label="Cerrar menú"
          >
            <X />
          </button>
        </div>
        <div className="admin-sidebar__identity">
          <ShieldCheck />
          <div>
            <strong>Consola de plataforma</strong>
            <small>Entorno SaaS independiente</small>
          </div>
        </div>
        <nav aria-label="Navegación de súper administración">
          <p className="nav-label">Plataforma</p>
          {navigation.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end ?? false}
              onClick={() => setMobileMenu(false)}
              className={({ isActive }) =>
                isActive ? "nav-item nav-item--active" : "nav-item"
              }
            >
              <item.icon />
              {item.label}
            </NavLink>
          ))}
        </nav>
        <div className="sidebar__bottom">
          <div className="system-health">
            <span className="health-dot" />
            Plataforma operativa
          </div>
          <NavLink className="nav-item" to="/account" onClick={() => setMobileMenu(false)}>
            <CircleUserRound /> Cuenta y privacidad
          </NavLink>
          <button className="nav-item nav-button" onClick={() => void logout()}>
            <LogOut />
            Cerrar sesión
          </button>
        </div>
      </aside>
      <div className="admin-area">
        <header className="admin-topbar">
          <button
            className="icon-button mobile-only"
            onClick={() => setMobileMenu(true)}
            aria-label="Abrir menú"
          >
            <Menu />
          </button>
          <div>
            <strong>RestLab Platform</strong>
            <small>Administración global</small>
          </div>
          <div className="user-menu">
            <span className="avatar">
              {user?.name
                .split(" ")
                .map((part) => part[0])
                .slice(0, 2)
                .join("")}
            </span>
            <span className="desktop-only">
              <strong>{user?.name}</strong>
              <small>Súper administrador</small>
            </span>
          </div>
        </header>
        {impersonation.active && (
          <div className="admin-impersonation-warning">
            <ShieldCheck />
            <span>
              Hay una impersonación activa de{" "}
              <strong>
                {impersonation.locationName ?? impersonation.organizationName}
              </strong>
              .
            </span>
            <button
              className="button button--outline"
              onClick={() => void exitImpersonation()}
            >
              Finalizar acceso
            </button>
          </div>
        )}
        <main id="admin-main" className="admin-main" tabIndex={-1}>
          {children}
        </main>
      </div>
    </div>
  );
}
